import { useTranslation } from 'react-i18next';
import {
  AccordionDetails,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import moment from 'moment';
import { UsersMultiSelect } from '../../../../components/UsersMultiSelect';
import { AccountData, OrderData, OrderStatus, PropelUserModel } from '../../../../../api';
import { Accordion, AccordionPanelsNames, AccordionSummary } from '../Accordion';
import { ManagementPanelSummary } from './ManagementPanelSummary';
import CopyToClipboardButton from '../../../CopyToClipboardButton';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { getDateFormat } from '../../../../../common/getDateAndTimeFormat';
import { ResourceSelectionPanel } from '../resourceSelectionPanel/ResourceSelectionPanel';

type ManagementPanelProps = {
  isEventOrderPage: boolean;
  users: PropelUserModel[];
  selectedUsersIds: string[];
  setSelectedUsersIds: (ids: string[]) => void;
  comment?: string;
  setComment: (comment: string) => void;
  expanded?: AccordionPanelsNames;
  handleChange: any;
  orderId?: string;
  isOneOfVoucherIntents: boolean;
  shouldUnarchive: boolean;
  setShouldUnarchive: (shouldUnarchive: boolean) => void;
  orderOrVoucher: Partial<OrderData>;
  orderPageId?: string;
  persons?: number;
  selectedResourceIds?: number[];
  setSelectedResourceIds?: (ids: number[]) => void;
};
export const ManagementPanel = ({
  isEventOrderPage,
  users,
  selectedUsersIds,
  setSelectedUsersIds,
  comment,
  setComment,
  expanded,
  handleChange,
  orderId,
  isOneOfVoucherIntents,
  shouldUnarchive,
  setShouldUnarchive,
  orderOrVoucher,
  orderPageId,
  persons,
  selectedResourceIds = [],
  setSelectedResourceIds,
}: ManagementPanelProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [internalComment, setInternalComment] = useState(comment);

  return (
    <Accordion onChange={handleChange} expandedPanelName={expanded} panelName={AccordionPanelsNames.ManagementPanel}>
      <AccordionSummary
        title={t('calendar.edit_order.magmntSection')}
        summaryComponent={
          <ManagementPanelSummary
            orderStatus={orderOrVoucher.status}
            shouldHideUsers={isOneOfVoucherIntents}
            comment={internalComment}
            selectedUsersIds={selectedUsersIds}
            users={users}
            isEventOrderPage={isEventOrderPage}
          />
        }
      />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={2}>
          {!isEventOrderPage && !isOneOfVoucherIntents && (
            <UsersMultiSelect
              users={users}
              selectedUsersIds={selectedUsersIds}
              setSelectedUsersIds={setSelectedUsersIds}
            />
          )}

          {!isEventOrderPage && !isOneOfVoucherIntents && orderPageId && persons && setSelectedResourceIds && (
            <>
              <Divider sx={{ my: 1 }} />
              <ResourceSelectionPanel
                orderPageId={orderPageId}
                persons={persons}
                selectedResourceIds={selectedResourceIds}
                setSelectedResourceIds={setSelectedResourceIds}
                disabled={!!orderId && orderOrVoucher?.status === OrderStatus.ARCHIVED}
              />
            </>
          )}

          <TextField
            multiline
            label={t('calendar.edit_event.comments')}
            type="text"
            value={comment}
            onChange={(e) => {
              setInternalComment(e.target.value);
              setComment(e.target.value);
            }}
          />
          {orderId && (authState.account as unknown as AccountData).returning_customer_settings && (
            <Grid container gap={0.5} flexDirection="column" borderRadius="8px" border="1px solid #c4c4c4" p={1}>
              <Typography fontWeight={700}>{`${t('calendar.order_popper.returningCsutomer')}`}</Typography>
              <Grid container gap={1} alignItems="center">
                <Typography>{orderId}</Typography>
                <CopyToClipboardButton width={'18px'} color="grey" text={orderId} />
              </Grid>
            </Grid>
          )}
          {orderId && (
            <Grid container gap={0.5} flexDirection="column" borderRadius="8px" border="1px solid #c4c4c4" p={1}>
              <Typography fontWeight={700}>{t('calendar.edit_order.status')}</Typography>
              <Typography> {t(`order_status.${orderOrVoucher?.status?.toLowerCase()}`)}</Typography>
              {orderOrVoucher?.status === OrderStatus.ARCHIVED && (
                <>
                  {orderOrVoucher.archived_by_user_id && (
                    <Typography variant="body2" color="text.secondary">
                      {t('calendar.edit_order.archived_by')}:{' '}
                      {(() => {
                        const archivedByUser = users.find((u) => u.user_id === orderOrVoucher.archived_by_user_id);
                        return archivedByUser
                          ? `${archivedByUser.first_name} ${archivedByUser.last_name}`
                          : orderOrVoucher.archived_by_user_id;
                      })()}
                    </Typography>
                  )}
                  {orderOrVoucher.archive_timestamp && (
                    <Typography variant="body2" color="text.secondary">
                      {t('calendar.edit_order.archived_at')}:{' '}
                      {moment(orderOrVoucher.archive_timestamp).format(getDateFormat(account).longDateFormat)}
                    </Typography>
                  )}
                  <FormControlLabel
                    control={<Checkbox />}
                    checked={shouldUnarchive}
                    label={
                      <Grid container gap={1}>
                        <Typography>{t('calendar.edit_order.changeBackTo')}</Typography>
                        <Typography fontWeight={700}>
                          {t(`order_status.${orderOrVoucher?.last_status?.toLowerCase()}`)}
                        </Typography>
                      </Grid>
                    }
                    onChange={() => {
                      setShouldUnarchive(!shouldUnarchive);
                    }}
                  />
                </>
              )}
            </Grid>
          )}

          {orderOrVoucher && (
            <Grid container gap={0.5} flexDirection="column" borderRadius="8px" border="1px solid #c4c4c4" p={1}>
              <Typography fontWeight={700}>{t('calendar.edit_order.utm')}</Typography>
              {orderOrVoucher?.utm_data &&
                Object.entries(orderOrVoucher.utm_data).map(([key, value]) => (
                  <Grid container key={key} alignItems="center" wrap="nowrap">
                    <Typography>{`${key.split('_').join(' ')}: `}</Typography>
                    <Typography
                      sx={{
                        maxWidth: isSmallScreen ? '150px' : '300px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'copy',
                      }}
                      onClick={() => navigator.clipboard.writeText(String(value))}
                      title={`${String(value)} (click to copy)`}>
                      {String(value)}
                    </Typography>
                  </Grid>
                ))}
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
